import React, { FC, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Container,
    Grid,
    Typography,
    TextField,
    MenuItem,
    Select,
    FormControl,
    CircularProgress,
    InputAdornment,
    IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import _ from 'lodash';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { IContest, IProject } from '../../../types/global/helper';
import { IPagination } from '../../../hook/usePagination';
import { isEmpty } from '../../../utils/lodash';
import { IContestSubmissionsResponse } from '../../../redux/contest/types';
import { CustomPagination } from '../../Tables/Pagination';
import { CinematicEntryCard } from '../../ContestSubmissions/CinematicEntryCard';
import { ContestDetailHero } from '../../ContestDetailsV2/ContestDetailHero';
import { calcTotalPool, formatContestPool, withAlpha } from '../../ContestDetailsV2/utils';

interface IContestSubmissionsContainerProps {
    contest: IContest | null;
    submissions: IContestSubmissionsResponse['data'];
    loading: boolean;
    searching: boolean;
    error: any;
    pagination: IPagination;
    searchTerm?: string;
    sortOption: string;
    orderOption: 'asc' | 'desc';
    availableFilters: { filter: string; display: string }[];
    onSearchChange: (searchText: string) => void;
    onSortOptionChange: (option: string) => void;
    onOrderChange: (order: 'asc' | 'desc') => void;
    onRowsChange: (rows: number) => void;
    onPageChange: (page: number) => void;
    onPlay?: (project: IProject) => void;
    onClearSearch: () => void;
    onBackButtonPressed?: () => void;
}

const PRIMARY = '#ffc63e';
const SURFACE_LOW = '#0e0e0e';
const SURFACE_HIGH = '#2a2a2a';
const OUTLINE_VAR = 'rgba(77, 70, 53, 0.2)';

function daysUntilEnd(iso?: string) {
    if (!iso) return null;
    const end = new Date(iso).getTime();
    if (Number.isNaN(end)) return null;
    const d = Math.ceil((end - Date.now()) / 86400000);
    return Math.max(0, d);
}

const selectPaperSx = (t: (typeof theme)['dark']) => ({
    bgcolor: t.secondaryBg,
    color: t.tag_white,
    fontFamily: FONT_ENUM.MAIN_MEDIUM,
    '& .MuiMenuItem-root': {
        '&:hover': { backgroundColor: t.highlight_to_light_grey },
        '&.Mui-selected': { backgroundColor: t.role_hover },
    },
});

export const ContestSubmissionsContainer: FC<IContestSubmissionsContainerProps> = ({
    contest,
    submissions,
    loading,
    searching,
    error: _submissionsError,
    pagination,
    searchTerm,
    sortOption,
    orderOption,
    availableFilters,
    onSearchChange,
    onSortOptionChange,
    onOrderChange,
    onRowsChange,
    onPageChange,
    onPlay,
    onClearSearch,
    onBackButtonPressed,
}) => {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const [value, setValue] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);
    const entriesAnchorRef = useRef<HTMLDivElement>(null);

    const debouncedSearchHandler = useRef(
        _.debounce((searchText: string) => {
            onSearchChange(searchText);
        }, 500),
    ).current;

    const handleClearSearch = () => {
        setValue('');
        onClearSearch();
    };

    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setValue(newValue);
        debouncedSearchHandler(newValue);
    };

    const scrollToEntries = () => {
        entriesAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const heroShellSx = {
        position: 'relative' as const,
        height: { xs: 400, md: 440 },
        width: '100%',
        borderRadius: 0,
        borderBottomLeftRadius: '12px',
        borderBottomRightRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'rgba(255,255,255,0.06)',
    };

    const pool = contest ? calcTotalPool(contest) : 0;
    const daysLeft = daysUntilEnd(contest?.expiration_date);
    const totalEntries = pagination.totalCount ?? 0;

    const sortComboValue = `${sortOption}::${orderOption}`;
    const sortCombos = useMemo(() => {
        const filters =
            availableFilters.length > 0 ? availableFilters : [{ filter: sortOption || 'date', display: 'Date' }];
        const out: { value: string; label: string }[] = [];
        for (const f of filters) {
            const disp = (f.display || f.filter).replace(/^sort\s*by:?\s*/i, '').trim();
            out.push(
                { value: `${f.filter}::desc`, label: `Sort by: ${disp} · Newest` },
                { value: `${f.filter}::asc`, label: `Sort by: ${disp} · Oldest` },
            );
        }
        if (!out.some(o => o.value === sortComboValue)) {
            out.unshift({
                value: sortComboValue,
                label: `Sort: ${sortOption} (${orderOption === 'desc' ? 'newest' : 'oldest'})`,
            });
        }
        return out;
    }, [availableFilters, sortOption, orderOption, sortComboValue]);

    const handleSortComboChange = (v: string) => {
        const parts = v.split('::');
        if (parts.length === 2) {
            onSortOptionChange(parts[0]);
            onOrderChange(parts[1] as 'asc' | 'desc');
        }
    };

    const glassCardSx = {
        background: 'rgba(42, 42, 42, 0.4)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderTop: `1px solid ${OUTLINE_VAR}`,
        borderLeft: `1px solid ${OUTLINE_VAR}`,
    };

    return (
        <Container
            maxWidth={false}
            disableGutters
            sx={{
                overflowX: 'visible',
                mt: 0,
                pt: 0,
                pb: { xs: 4, sm: 6 },
                px: { xs: 2, sm: 3, lg: 4 },
                ml: 0,
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                minHeight: '100vh',
                bgcolor: '#131313',
            }}>
            <Box
                sx={{
                    mx: { xs: -2, sm: -3, lg: -4 },
                    width: { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)', lg: 'calc(100% + 64px)' },
                    mb: { xs: 4, sm: 6 },
                }}>
                {contest ? (
                    <ContestDetailHero contest={contest} onBackButtonPressed={onBackButtonPressed} />
                ) : (
                    <Box sx={heroShellSx} />
                )}
            </Box>

            <Box
                sx={{
                    width: '100%',
                    boxSizing: 'border-box',
                }}>
                <Box
                    sx={{
                        maxWidth: 1152,
                        width: '100%',
                        minWidth: 0,
                        mx: 'auto',
                        boxSizing: 'border-box',
                    }}>
                    {/* Search & filters */}
                    <Box
                        ref={entriesAnchorRef}
                        id="contest-submissions-entries"
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'stretch', md: 'center' },
                            gap: 2,
                            mb: 6,
                        }}>
                        <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
                            <TextField
                                inputRef={searchInputRef}
                                fullWidth
                                variant="outlined"
                                placeholder="Search entries, creators, or tags..."
                                value={value}
                                onChange={handleSearchInputChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: withAlpha(t.true_white, 0.4), ml: 0.5 }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: value ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={handleClearSearch}
                                                edge="end"
                                                sx={{ mr: 0.5 }}>
                                                <CloseIcon sx={{ fontSize: 20, color: withAlpha(t.true_white, 0.5) }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5,
                                        py: 1.25,
                                        pl: 1,
                                        minHeight: 56,
                                        bgcolor: SURFACE_LOW,
                                        color: t.tag_white,
                                        fontFamily: 'Inter, system-ui, sans-serif',
                                        '& fieldset': { borderColor: OUTLINE_VAR },
                                        '&:hover fieldset': { borderColor: withAlpha(PRIMARY, 0.35) },
                                        '&.Mui-focused fieldset': {
                                            borderColor: PRIMARY,
                                            borderWidth: 1,
                                        },
                                    },
                                }}
                            />
                            {searching && (
                                <CircularProgress
                                    size={22}
                                    sx={{
                                        position: 'absolute',
                                        right: 48,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: PRIMARY,
                                    }}
                                />
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexShrink: 0, width: { xs: '100%', md: 'auto' } }}>
                            <Button
                                aria-label="Focus search filters"
                                onClick={() => searchInputRef.current?.focus()}
                                startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
                                sx={{
                                    flex: { xs: 1, md: 'unset' },
                                    borderRadius: 1.5,
                                    px: 3,
                                    py: 1.75,
                                    border: `1px solid ${withAlpha('#4d4635', 0.35)}`,
                                    bgcolor: SURFACE_HIGH,
                                    color: t.tag_white,
                                    textTransform: 'none',
                                    fontFamily: 'Inter, system-ui, sans-serif',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: '0.14em',
                                    '&:hover': { bgcolor: withAlpha('#393939', 0.35) },
                                }}>
                                FILTER
                            </Button>
                            <FormControl
                                size="small"
                                sx={{
                                    flex: { xs: 1, md: 280 },
                                    minWidth: { md: 260 },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5,
                                        bgcolor: SURFACE_HIGH,
                                        color: t.tag_white,
                                        minHeight: 56,
                                        py: 0.5,
                                        '& fieldset': { borderColor: withAlpha('#4d4635', 0.35) },
                                        '&:hover fieldset': { borderColor: withAlpha(t.true_white, 0.12) },
                                    },
                                    '& .MuiSvgIcon-root': { color: withAlpha(t.true_white, 0.45) },
                                }}>
                                <Select
                                    value={
                                        sortCombos.some(c => c.value === sortComboValue)
                                            ? sortComboValue
                                            : sortCombos[0]?.value
                                    }
                                    onChange={e => handleSortComboChange(e.target.value as string)}
                                    IconComponent={ExpandMoreIcon}
                                    displayEmpty
                                    renderValue={v => {
                                        const opt = sortCombos.find(c => c.value === v);
                                        return opt?.label ?? 'Sort';
                                    }}
                                    MenuProps={{ PaperProps: { sx: selectPaperSx(t) } }}
                                    sx={{
                                        borderRadius: 1.5,
                                        fontFamily: 'Inter, system-ui, sans-serif',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        letterSpacing: '0.08em',
                                        pl: 1,
                                        pr: 1,
                                    }}>
                                    {sortCombos.map(c => (
                                        <MenuItem key={c.value} value={c.value}>
                                            {c.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    {/* Section header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
                        <Typography
                            sx={{
                                fontFamily: '"Manrope", system-ui, sans-serif',
                                fontWeight: 700,
                                fontSize: '1.5rem',
                                letterSpacing: '-0.02em',
                                color: t.tag_white,
                                flexShrink: 0,
                                textTransform: 'uppercase',
                            }}>
                            Featured entries
                        </Typography>
                        <Box
                            sx={{
                                flex: 1,
                                height: '1px',
                                bgcolor: withAlpha('#4d4635', 0.35),
                                display: { xs: 'none', md: 'block' },
                            }}
                        />
                        <Typography
                            component="button"
                            type="button"
                            onClick={scrollToEntries}
                            sx={{
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.2em',
                                color: PRIMARY,
                                flexShrink: 0,
                                textTransform: 'uppercase',
                                '&:hover': { textDecoration: 'underline' },
                            }}>
                            View all
                        </Typography>
                    </Box>

                    {loading && !searching ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                            <CircularProgress size={44} sx={{ color: PRIMARY }} />
                        </Box>
                    ) : searching && isEmpty(submissions) ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                            <CircularProgress size={44} sx={{ color: PRIMARY }} />
                        </Box>
                    ) : isEmpty(submissions) ? (
                        <Box sx={{ textAlign: 'center', py: 10 }}>
                            <Typography sx={{ color: t.filters_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                No submissions found
                            </Typography>
                            {searchTerm && (
                                <Typography
                                    onClick={onClearSearch}
                                    sx={{
                                        color: PRIMARY,
                                        cursor: 'pointer',
                                        mt: 2,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Clear search
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <Grid
                            container
                            spacing={4}
                            sx={{
                                opacity: searching ? 0.55 : 1,
                                transition: 'opacity 0.2s ease',
                                pointerEvents: searching ? 'none' : 'auto',
                            }}>
                            {submissions.map(
                                submission =>
                                    submission &&
                                    submission.project && (
                                        <Grid item xs={12} sm={6} md={4} key={submission._id}>
                                            <CinematicEntryCard
                                                project={submission.project}
                                                user={submission.user}
                                                totalVotes={submission.total_votes}
                                                onPlay={() => {
                                                    const payload: any = {
                                                        ...submission.project,
                                                        submissions: [{ ...submission, contest }],
                                                    };
                                                    onPlay?.(payload);
                                                }}
                                            />
                                        </Grid>
                                    ),
                            )}
                        </Grid>
                    )}

                    {!isEmpty(submissions) && pagination.totalCount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                            <CustomPagination
                                pagination={pagination}
                                rowsPerPageOptions={[6, 9, 12, 24]}
                                onRowsChange={onRowsChange}
                                onPageChange={onPageChange}
                            />
                        </Box>
                    )}

                    {/* Stats row */}
                    {contest && (
                        <Grid container spacing={3} sx={{ mt: 2, mb: 6 }}>
                            <Grid item xs={12} md={6}>
                                <Box
                                    sx={{
                                        ...glassCardSx,
                                        borderRadius: 1.5,
                                        p: { xs: 3, sm: 4 },
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        minHeight: 220,
                                    }}>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Inter, system-ui, sans-serif',
                                                fontSize: 12,
                                                letterSpacing: '0.25em',
                                                fontWeight: 700,
                                                color: PRIMARY,
                                                textTransform: 'uppercase',
                                            }}>
                                            Upcoming live
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: '"Manrope", system-ui, sans-serif',
                                                fontWeight: 700,
                                                fontSize: { xs: '1.75rem', sm: '2rem' },
                                                color: t.tag_white,
                                                mt: 1,
                                                lineHeight: 1.15,
                                            }}>
                                            {contest.title}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            mt: 4,
                                            display: 'flex',
                                            alignItems: 'flex-end',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            flexWrap: 'wrap',
                                        }}>
                                        <Typography
                                            sx={{
                                                fontSize: 14,
                                                color: withAlpha(t.tag_white, 0.55),
                                                maxWidth: 220,
                                                fontFamily: 'Inter, system-ui, sans-serif',
                                            }}>
                                            Winners announced when the campaign ends. Keep voting for your favorites.
                                        </Typography>
                                        {daysLeft != null && (
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: '"Manrope", system-ui, sans-serif',
                                                        fontWeight: 900,
                                                        fontSize: '2.25rem',
                                                        lineHeight: 1,
                                                        color: t.tag_white,
                                                    }}>
                                                    {daysLeft}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: 10,
                                                        letterSpacing: '0.2em',
                                                        color: withAlpha(t.tag_white, 0.35),
                                                        textTransform: 'uppercase',
                                                        mt: 0.5,
                                                    }}>
                                                    Days remaining
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Box
                                    sx={{
                                        borderRadius: 1.5,
                                        p: 4,
                                        height: '100%',
                                        minHeight: 200,
                                        bgcolor: SURFACE_HIGH,
                                        border: `1px solid ${OUTLINE_VAR}`,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                    }}>
                                    <GroupsIcon sx={{ fontSize: 40, color: PRIMARY, mb: 2 }} />
                                    <Typography
                                        sx={{
                                            fontFamily: '"Manrope", system-ui, sans-serif',
                                            fontWeight: 700,
                                            fontSize: '1.5rem',
                                            color: t.tag_white,
                                        }}>
                                        {totalEntries.toLocaleString()}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            letterSpacing: '0.25em',
                                            color: withAlpha(t.tag_white, 0.35),
                                            textTransform: 'uppercase',
                                            mt: 1,
                                            fontFamily: 'Inter, system-ui, sans-serif',
                                        }}>
                                        Active voters
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Box
                                    sx={{
                                        borderRadius: 1.5,
                                        p: 4,
                                        height: '100%',
                                        minHeight: 200,
                                        bgcolor: SURFACE_HIGH,
                                        border: `1px solid ${OUTLINE_VAR}`,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                    }}>
                                    <EmojiEventsIcon sx={{ fontSize: 40, color: PRIMARY, mb: 2 }} />
                                    <Typography
                                        sx={{
                                            fontFamily: '"Manrope", system-ui, sans-serif',
                                            fontWeight: 700,
                                            fontSize: '1.5rem',
                                            color: t.tag_white,
                                        }}>
                                        {pool > 0 ? formatContestPool(contest) : '—'}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            letterSpacing: '0.25em',
                                            color: withAlpha(t.true_white, 0.35),
                                            textTransform: 'uppercase',
                                            mt: 1,
                                            fontFamily: 'Inter, system-ui, sans-serif',
                                        }}>
                                        Prize pool
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </Box>
        </Container>
    );
};
